import React from 'react';
import { Lead } from '../types/leads';
import { XIcon, AlertTriangleIcon } from 'lucide-react';
interface PushConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedLeads: Lead[];
}
export function PushConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  selectedLeads
}: PushConfirmationDialogProps) {
  if (!isOpen) return null;
  // Group selected leads by expected dialing list
  const listBreakdown = selectedLeads.reduce((acc, lead) => {
    acc[lead.expectedDialingList] = (acc[lead.expectedDialingList] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  return <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" data-id="element-186">
      {/* Backdrop */}
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-187">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-188"></div>

        {/* Center modal trick */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-189">
          &#8203;
        </span>

        {/* Modal Panel */}
        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-190">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-191">
            <div className="sm:flex sm:items-start" data-id="element-192">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-193">
                <AlertTriangleIcon className="h-6 w-6 text-blue-600" aria-hidden="true" data-id="element-194" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-195">
                <div className="flex justify-between items-center mb-2" data-id="element-196">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-197">
                    Push Leads to Dialing Lists
                  </h3>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-198">
                    <XIcon className="h-5 w-5" data-id="element-199" />
                  </button>
                </div>

                <div className="mt-2" data-id="element-200">
                  <p className="text-sm text-gray-500 mb-4" data-id="element-201">
                    You are about to push{' '}
                    <span className="font-bold text-gray-900" data-id="element-202">
                      {selectedLeads.length}
                    </span>{' '}
                    leads to their assigned dialing lists.
                  </p>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-4" data-id="element-203">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-204">
                      Target Lists Summary
                    </h4>
                    <ul className="space-y-2" data-id="element-205">
                      {Object.entries(listBreakdown).map(([listName, count]) => <li key={listName} className="flex justify-between text-sm" data-id="element-206">
                            <span className="text-gray-700 font-medium" data-id="element-207">
                              {listName}
                            </span>
                            <span className="text-gray-500" data-id="element-208">
                              {count} lead{count !== 1 ? 's' : ''}
                            </span>
                          </li>)}
                    </ul>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-100" data-id="element-209">
                    <div className="flex items-start gap-1.5" data-id="element-210">
                      <span className="mt-0.5 flex-shrink-0" data-id="element-211">ℹ️</span>
                      <p className="text-xs text-blue-700" data-id="element-212">
                        Leads will enter a{' '}
                        <span className="font-semibold" data-id="element-213">
                          Pending Verification
                        </span>{' '}
                        state while the external system confirms list placement.
                        Status will update to{' '}
                        <span className="font-semibold" data-id="element-214">Verified</span> once
                        confirmed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200" data-id="element-215">
            <button type="button" className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => {
            onConfirm();
            onClose();
          }} data-id="element-216">
              Confirm & Push to Lists
            </button>
            <button type="button" className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={onClose} data-id="element-217">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>;
}